import React, { useMemo } from "react";

import { faRobot } from "@fortawesome/pro-regular-svg-icons";

import { ExecutionLogView } from "src/agents/AgentVersionPage/AgentPlayground";
import { useAgentSession } from "src/agents/queries/execution";
import type { SessionResponseMessagesInner } from "src/clients/agent-management";
import type {
  AgentNode,
  NodeHistoricalRunStateV2,
} from "src/constants/NodeDataTypes";
import { EmptyState } from "src/design-system/EmptyState";

type AgentTraceTabProps = {
  node: AgentNode;
  runState: NodeHistoricalRunStateV2;
};

export const AgentTraceTab: React.FC<AgentTraceTabProps> = ({
  node,
  runState,
}) => {
  const sessionId =
    runState.nodeExecutionMetadata?.[node.id]?.agent_session_id ?? null;
  const sessionQuery = useAgentSession(sessionId);

  // Extract the first human message to use as optimistic input message
  const optimisticInputMessage =
    useMemo((): SessionResponseMessagesInner | null => {
      const messages = sessionQuery.data?.messages;
      if (!messages) return null;
      return messages.find((msg) => msg.type === "human") ?? null;
    }, [sessionQuery.data?.messages]);

  if (!sessionId) {
    return (
      <EmptyState
        description="Agent trace is not available for this decision."
        headline="No agent trace available"
        icon={faRobot}
      />
    );
  }

  if (sessionQuery.isLoading) {
    return (
      <div className="p-4 font-inter-normal-13px text-gray-500">
        Loading agent trace...
      </div>
    );
  }

  if (sessionQuery.isError || !sessionQuery.data) {
    return (
      <EmptyState
        description="Agent trace could not be loaded."
        headline="No agent trace available"
        icon={faRobot}
      />
    );
  }

  const { status, termination_reason, messages, result } = sessionQuery.data;

  return (
    <div className="flex h-full flex-col px-5">
      <ExecutionLogView
        agentName={node.data.label}
        messages={messages ?? []}
        optimisticInputMessage={optimisticInputMessage}
        rawSessionData={sessionQuery.data}
        result={result}
        sessionStatus={status}
        terminationReason={termination_reason}
      />
    </div>
  );
};
